import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaYoutube, FaTrash, FaEdit, FaPlus, FaSave, FaTimes, FaStar, FaPlay } from 'react-icons/fa';
import { useLocalStorage } from '../../hooks/useLocalStorage';
import { videos as initialVideos } from '../../data/videos';

const VideoLinkManager = () => {
  const [videos, setVideos] = useLocalStorage('tinytimes-videos', initialVideos);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    youtubeId: '',
    category: 'stories',
    duration: '',
    featured: false,
  });

  const categories = [
    { id: 'stories', label: '📖 Stories' },
    { id: 'puzzles', label: '🧩 Puzzles' },
    { id: 'activities', label: '🎨 Activities' },
    { id: 'behindthescenes', label: '🎬 Behind the Scenes' },
    { id: 'tutorials', label: '📝 Tutorials' },
  ];

  // Extract YouTube ID from URL
  const extractYoutubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : url;
  };

  // Add new video
  const handleAddVideo = () => {
    if (!newVideo.title.trim() || !newVideo.youtubeId.trim()) {
      alert('Please enter a title and YouTube video ID or URL');
      return;
    }

    const video = {
      id: Date.now(),
      ...newVideo,
      youtubeId: extractYoutubeId(newVideo.youtubeId),
      publishedAt: new Date().toISOString(),
      views: '0',
    };

    setVideos(prev => [...prev, video]);
    setNewVideo({
      title: '',
      description: '',
      youtubeId: '',
      category: 'stories',
      duration: '',
      featured: false,
    });
    setIsAddingNew(false);
  };

  // Delete video
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      setVideos(prev => prev.filter(v => v.id !== id));
    }
  };

  // Toggle featured
  const toggleFeatured = (id) => {
    setVideos(prev => prev.map(v => 
      v.id === id ? { ...v, featured: !v.featured } : v
    ));
  };

  // Update video
  const handleUpdate = (id, updates) => {
    setVideos(prev => prev.map(v => 
      v.id === id ? { ...v, ...updates, youtubeId: extractYoutubeId(updates.youtubeId || v.youtubeId) } : v
    ));
    setEditingId(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-display font-bold text-primary-green-dark">
          Video Management
        </h2>
        <motion.button
          onClick={() => setIsAddingNew(true)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="btn-primary flex items-center gap-2"
        >
          <FaPlus />
          <span>Add Video</span>
        </motion.button>
      </div>

      {/* Add New Video Form */}
      <AnimatePresence>
        {isAddingNew && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="card bg-red-50 border-2 border-red-200"
          >
            <div className="flex items-center gap-3 mb-4">
              <FaYoutube className="text-3xl text-red-500" />
              <h3 className="text-xl font-display font-bold text-primary-green-dark">
                Add New YouTube Video
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                <div>
                  <label className="block font-semibold text-primary-green-dark mb-2">
                    Video Title *
                  </label>
                  <input
                    type="text"
                    value={newVideo.title}
                    onChange={(e) => setNewVideo(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Enter video title"
                    className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block font-semibold text-primary-green-dark mb-2">
                    YouTube Video ID or URL *
                  </label>
                  <input
                    type="text"
                    value={newVideo.youtubeId}
                    onChange={(e) => setNewVideo(prev => ({ ...prev, youtubeId: e.target.value }))}
                    placeholder="e.g., dQw4w9WgXcQ or full YouTube URL"
                    className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
                  />
                  <p className="text-sm text-neutral-gray-dark mt-1">
                    Paste the video ID or the full YouTube URL
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-semibold text-primary-green-dark mb-2">
                      Category
                    </label>
                    <select
                      value={newVideo.category}
                      onChange={(e) => setNewVideo(prev => ({ ...prev, category: e.target.value }))}
                      className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none bg-white"
                    >
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block font-semibold text-primary-green-dark mb-2">
                      Duration
                    </label>
                    <input
                      type="text"
                      value={newVideo.duration}
                      onChange={(e) => setNewVideo(prev => ({ ...prev, duration: e.target.value }))}
                      placeholder="e.g., 5:30"
                      className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-4">
                <div>
                  <label className="block font-semibold text-primary-green-dark mb-2">
                    Description
                  </label>
                  <textarea
                    value={newVideo.description}
                    onChange={(e) => setNewVideo(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Brief description of the video..."
                    rows={4}
                    className="w-full px-4 py-3 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
                  />
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newVideo.featured}
                    onChange={(e) => setNewVideo(prev => ({ ...prev, featured: e.target.checked }))}
                    className="w-5 h-5 accent-primary-green"
                  />
                  <span className="font-semibold text-primary-green-dark">
                    ⭐ Featured Video
                  </span>
                </label>

                {/* Preview */}
                {newVideo.youtubeId && (
                  <div>
                    <label className="block font-semibold text-primary-green-dark mb-2">
                      Preview
                    </label>
                    <div className="aspect-video bg-black rounded-lg overflow-hidden">
                      <img
                        src={`https://img.youtube.com/vi/${extractYoutubeId(newVideo.youtubeId)}/hqdefault.jpg`}
                        alt="Video thumbnail"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <motion.button
                onClick={handleAddVideo}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="btn-primary flex items-center gap-2"
              >
                <FaSave />
                <span>Save Video</span>
              </motion.button>
              <motion.button
                onClick={() => {
                  setIsAddingNew(false);
                  setNewVideo({ title: '', description: '', youtubeId: '', category: 'stories', duration: '', featured: false });
                }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-button font-semibold flex items-center gap-2"
              >
                <FaTimes />
                <span>Cancel</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Videos Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => (
          <motion.div
            key={video.id}
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="card p-0 overflow-hidden group"
          >
            {editingId === video.id ? (
              <EditVideoForm
                video={video}
                categories={categories}
                onSave={(updates) => handleUpdate(video.id, updates)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <>
                {/* Thumbnail */}
                <div className="relative aspect-video">
                  <img
                    src={`https://img.youtube.com/vi/${video.youtubeId}/hqdefault.jpg`}
                    alt={video.title}
                    className="w-full h-full object-cover"
                  />

                  {/* Play Button Overlay */}
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center">
                      <FaPlay className="text-white text-xl ml-1" />
                    </div>
                  </div>

                  {/* Badges */}
                  {video.featured && (
                    <div className="absolute top-2 right-2 bg-accent-yellow text-primary-green-dark text-xs font-bold px-2 py-1 rounded-full">
                      ⭐ Featured
                    </div>
                  )}
                  
                  {video.duration && (
                    <div className="absolute bottom-2 right-2 bg-black/80 text-white text-xs px-2 py-1 rounded">
                      {video.duration}
                    </div>
                  )}

                  <div className="absolute top-2 left-2 bg-primary-green text-white text-xs font-bold px-2 py-1 rounded-full capitalize">
                    {video.category}
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <h3 className="font-display font-bold text-primary-green-dark line-clamp-1 mb-1">
                    {video.title}
                  </h3>
                  <p className="text-sm text-neutral-gray-dark line-clamp-2 mb-3">
                    {video.description || 'No description'}
                  </p>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <a
                      href={`https://www.youtube.com/watch?v=${video.youtubeId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1 py-2 bg-red-100 text-red-600 rounded-lg text-sm font-semibold hover:bg-red-200 transition-colors"
                    >
                      <FaYoutube />
                      <span>Watch</span>
                    </a>
                    <button
                      onClick={() => setEditingId(video.id)}
                      className="p-2 bg-accent-orange/10 text-accent-orange rounded-lg hover:bg-accent-orange/20 transition-colors"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => toggleFeatured(video.id)}
                      className={`p-2 rounded-lg transition-colors ${
                        video.featured
                          ? 'bg-accent-yellow text-primary-green-dark'
                          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                      }`}
                    >
                      <FaStar />
                    </button>
                    <button
                      onClick={() => handleDelete(video.id)}
                      className="p-2 bg-red-100 text-red-500 rounded-lg hover:bg-red-200 transition-colors"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        ))}
      </div>

      {videos.length === 0 && (
        <div className="text-center py-20">
          <div className="text-8xl mb-4">🎬</div>
          <h3 className="text-2xl font-display font-bold text-primary-green-dark mb-2">
            No videos yet
          </h3>
          <p className="text-neutral-gray-dark">
            Add your first YouTube video to get started!
          </p>
        </div>
      )}
    </div>
  );
};

// Edit Video Form Component
const EditVideoForm = ({ video, categories, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    title: video.title,
    description: video.description || '',
    youtubeId: video.youtubeId,
    category: video.category,
    duration: video.duration || '',
    featured: video.featured,
  });

  return (
    <div className="p-4 space-y-3">
      <input
        type="text"
        value={formData.title}
        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
        placeholder="Video title"
        className="w-full px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
      />

      <input
        type="text"
        value={formData.youtubeId}
        onChange={(e) => setFormData(prev => ({ ...prev, youtubeId: e.target.value }))}
        placeholder="YouTube ID or URL"
        className="w-full px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
      />

      <textarea
        value={formData.description}
        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
        placeholder="Description..."
        rows={2}
        className="w-full px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none resize-none"
      />

      <div className="grid grid-cols-2 gap-2">
        <select
          value={formData.category}
          onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
          className="px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none bg-white"
        >
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.label}</option>
          ))}
        </select>

        <input
          type="text"
          value={formData.duration}
          onChange={(e) => setFormData(prev => ({ ...prev, duration: e.target.value }))}
          placeholder="Duration"
          className="px-3 py-2 border-2 border-primary-green/30 rounded-lg focus:border-primary-green focus:outline-none"
        />
      </div>

      <div className="flex gap-2">
        <motion.button
          onClick={() => onSave(formData)}
          whileHover={{ scale: 1.02 }}
          className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary-green text-white rounded-lg font-semibold"
        >
          <FaSave />
          <span>Save</span>
        </motion.button>
        <motion.button
          onClick={onCancel}
          whileHover={{ scale: 1.02 }}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold"
        >
          <FaTimes />
        </motion.button>
      </div>
    </div>
  );
};

export default VideoLinkManager;